import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getAllCarMakes, createCarMake, updateCarMake, deleteCarMake } from '../api/carMake';
import { getAllCarModels, createCarModel, updateCarModel, deleteCarModel } from '../api/carModel';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../utils/i18n-utils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`car-tabpanel-${index}`}
      aria-labelledby={`car-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface CarMake {
  id: number;
  name: string;
  description: string;
  active: boolean;
}

interface CarModel {
  id: number;
  name: string;
  description: string;
  active: boolean;
  carMakeId: number;
  yearStart?: number | null;
  yearEnd?: number | null;
  createdAt?: string;
  updatedAt?: string;
  carMake?: {
    id: number;
    name: string;
  };
  makeName?: string;
}

const CarManagement: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [makes, setMakes] = useState<CarMake[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [openMakeDialog, setOpenMakeDialog] = useState(false);
  const [openModelDialog, setOpenModelDialog] = useState(false);
  const [currentMake, setCurrentMake] = useState<CarMake | null>(null);
  const [currentModel, setCurrentModel] = useState<CarModel | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 5,
    page: 0,
  });
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [makeName, setMakeName] = useState('');
  const [makeDescription, setMakeDescription] = useState('');
  const [modelName, setModelName] = useState('');
  const [modelDescription, setModelDescription] = useState('');
  const [selectedMakeId, setSelectedMakeId] = useState<number | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      console.log("User is authenticated, fetching data...");
      // First fetch makes, then fetch models after makes are loaded
      fetchMakes().then(() => {
        fetchModels();
      });
    } else {
      console.warn("User is not authenticated!");
      showSnackbar('You must be logged in to view this page', 'error');
    }
  }, [isAuthenticated]);

  const fetchMakes = async () => {
    setLoading(true);
    try {
      console.log("Fetching car makes...");
      const data = await getAllCarMakes();
      console.log("Car makes fetched:", data);
      
      // Ensure all makes have the necessary properties
      const validatedMakes = Array.isArray(data) ? data.filter(make => 
        make && 
        typeof make === 'object' && 
        make.id !== undefined
      ) : [];
      
      console.log("Validated makes:", validatedMakes);
      setMakes(validatedMakes);
      return validatedMakes; // Return makes for chaining
    } catch (error: any) {
      console.error('Error fetching car makes:', error);
      if (error.response && error.response.status === 401) {
        showSnackbar('Your session has expired. Please log in again.', 'error');
        logout();
      } else {
        showSnackbar('Failed to fetch car makes: ' + (error.message || 'Unknown error'), 'error');
      }
      return []; // Return empty array if error
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    setLoading(true);
    try {
      console.log("Fetching car models...");
      const data = await getAllCarModels();
      console.log("Raw car models data:", JSON.stringify(data).substring(0, 500) + "...");
      
      // Ensure all models have the necessary properties
      const validatedModels = Array.isArray(data) ? data.filter(model => 
        model && 
        typeof model === 'object' && 
        model.id !== undefined
      ) : [];
      
      // Add explicit makeName field to each model
      const processedModels = validatedModels.map(model => {
        // First log the model's current car make info
        console.log(`Processing model ${model.id} (${model.name}):`, {
          carMakeId: model.carMakeId,
          carMake: model.carMake,
          hasCarMakeObj: !!model.carMake,
          hasCarMakeName: model.carMake?.name ? true : false
        });
        
        // Add a makeName field for direct display
        let makeName = 'Unknown';
        
        // First try to use the nested carMake object
        if (model.carMake && model.carMake.name) {
          makeName = model.carMake.name;
          console.log(`Model ${model.id}: Using carMake.name: ${makeName}`);
        }
        // Then try to lookup by ID
        else if (model.carMakeId) {
          const make = makes.find(m => m.id === model.carMakeId);
          if (make) {
            makeName = make.name;
            console.log(`Model ${model.id}: Using makes lookup: ${makeName}`);
          }
        }
        // Finally try to extract from description
        else if (model.description) {
          const match = model.description.match(/model by ([A-Za-z-]+)/);
          if (match && match[1]) {
            makeName = match[1];
            console.log(`Model ${model.id}: Extracted from description: ${makeName}`);
          }
        }
        
        return {
          ...model,
          makeName: makeName
        };
      });
      
      console.log("First processed model sample:", processedModels.length > 0 ? processedModels[0] : "No models");
      
      setModels(processedModels);
    } catch (error: any) {
      console.error('Error fetching car models:', error);
      if (error.response && error.response.status === 401) {
        showSnackbar('Your session has expired. Please log in again.', 'error');
        logout();
      } else {
        showSnackbar('Failed to fetch car models: ' + (error.message || 'Unknown error'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenMakeDialog = (make?: CarMake) => {
    if (make) {
      setCurrentMake(make);
      setMakeName(make.name);
      setMakeDescription(make.description || '');
      setIsEditing(true);
    } else {
      setCurrentMake(null);
      setMakeName('');
      setMakeDescription('');
      setIsEditing(false);
    }
    setOpenMakeDialog(true);
  };

  const handleOpenModelDialog = (model?: CarModel) => {
    if (model) {
      console.log("Opening model dialog for existing model:", model);
      setCurrentModel(model);
      setModelName(model.name);
      setModelDescription(model.description || '');
      
      // First try to get car make ID from the nested carMake object
      if (model.carMake && model.carMake.id) {
        setSelectedMakeId(model.carMake.id);
        console.log(`Selected car make from nested object: ${model.carMake.name} (ID: ${model.carMake.id})`);
      } 
      // Then try the direct carMakeId property
      else if (model.carMakeId !== undefined && model.carMakeId !== null) {
        setSelectedMakeId(model.carMakeId);
        
        // Log if the make exists in our makes array
        const make = makes.find(m => m.id === model.carMakeId);
        if (make) {
          console.log(`Selected car make: ${make.name} (ID: ${make.id})`);
        } else {
          console.warn(`Car make with ID ${model.carMakeId} not found in makes list`);
        }
      } else {
        // Try to infer car make from description if carMakeId is missing
        if (model.description) {
          const match = model.description.match(/model by ([A-Za-z-]+)/);
          if (match && match[1]) {
            const makeName = match[1];
            const inferredMake = makes.find(m => m.name.toLowerCase() === makeName.toLowerCase());
            if (inferredMake) {
              console.log(`Inferred car make ${inferredMake.name} from description`);
              setSelectedMakeId(inferredMake.id);
            } else {
              setSelectedMakeId(null);
            }
          } else {
            setSelectedMakeId(null);
          }
        } else {
          setSelectedMakeId(null);
        }
      }
      
      setIsEditing(true);
    } else {
      setCurrentModel(null);
      setModelName('');
      setModelDescription('');
      setSelectedMakeId(null);
      setIsEditing(false);
    }
    setOpenModelDialog(true);
  };

  const handleCloseMakeDialog = () => {
    setOpenMakeDialog(false);
  };

  const handleCloseModelDialog = () => {
    setOpenModelDialog(false);
  };

  const handleSaveMake = async () => {
    if (!makeName.trim()) {
      showSnackbar('Make name is required', 'error');
      return;
    }

    try {
      if (isEditing && currentMake) {
        // Update existing make
        await updateCarMake(currentMake.id, {
          name: makeName,
          description: makeDescription,
        });
        showSnackbar('Car make updated successfully', 'success');
      } else {
        // Create new make
        await createCarMake({
          name: makeName,
          description: makeDescription,
        });
        showSnackbar('Car make added successfully', 'success');
      }
      fetchMakes();
      handleCloseMakeDialog();
    } catch (error) {
      console.error('Error saving car make:', error);
      showSnackbar('Failed to save car make', 'error');
    }
  };

  const handleSaveModel = async () => {
    if (!modelName.trim()) {
      showSnackbar('Model name is required', 'error');
      return;
    }

    if (!selectedMakeId) {
      showSnackbar('Please select a car make', 'error');
      return;
    }

    // Verify the selected make exists in our makes list
    const selectedMake = makes.find(make => make.id === selectedMakeId);
    if (!selectedMake) {
      showSnackbar(`Invalid car make selected (ID: ${selectedMakeId})`, 'error');
      return;
    }
    
    // Create model data with its make association
    const modelData = {
      name: modelName,
      description: modelDescription,
      carMakeId: selectedMakeId,
      // Optionally add make name to description if not already included
      ...(modelDescription && !modelDescription.includes(selectedMake.name) ? {
        description: `${modelDescription} (${selectedMake.name})`
      } : {})
    };

    console.log("Saving car model with data:", modelData);

    try {
      if (isEditing && currentModel) {
        // Update existing model
        await updateCarModel(currentModel.id, modelData);
        showSnackbar('Car model updated successfully', 'success');
      } else {
        // Create new model
        await createCarModel(modelData);
        showSnackbar('Car model added successfully', 'success');
      }
      // After saving, first refresh makes (in case any new makes were added), then models
      await fetchMakes();
      await fetchModels();
      handleCloseModelDialog();
    } catch (error) {
      console.error('Error saving car model:', error);
      showSnackbar('Failed to save car model', 'error');
    }
  };

  const handleDeleteMake = async (makeId: number) => {
    if (window.confirm('Are you sure you want to delete this car make? This will also delete all associated models.')) {
      try {
        await deleteCarMake(makeId);
        showSnackbar('Car make deleted successfully', 'success');
        fetchMakes();
        fetchModels();
      } catch (error) {
        console.error('Error deleting car make:', error);
        showSnackbar('Failed to delete car make', 'error');
      }
    }
  };

  const handleDeleteModel = async (modelId: number) => {
    if (window.confirm('Are you sure you want to delete this car model?')) {
      try {
        await deleteCarModel(modelId);
        showSnackbar('Car model deleted successfully', 'success');
        fetchModels();
      } catch (error) {
        console.error('Error deleting car model:', error);
        showSnackbar('Failed to delete car model', 'error');
      }
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const makeColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: t('carManagement.name') || 'Name', width: 200 },
    { field: 'description', headerName: t('carManagement.description') || 'Description', width: 300 },
    { field: 'active', headerName: t('carManagement.active') || 'Active', width: 100, type: 'boolean' },
    {
      field: 'actions',
      headerName: t('common.actions') || 'Actions',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton size="small" onClick={() => params.row && handleOpenMakeDialog(params.row as CarMake)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => params.row && params.row.id && handleDeleteMake(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const modelColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: t('carManagement.name') || 'Name', width: 200 },
    { field: 'description', headerName: t('carManagement.description') || 'Description', width: 300 },
    { 
      field: 'makeName', 
      headerName: t('carManagement.carMakes') || 'Car Make', 
      width: 150,
      // Add a renderCell function as a fallback display mechanism
      renderCell: (params: GridRenderCellParams) => {
        console.log("Rendering car make cell:", params.row);
        if (params.row.makeName) {
          return params.row.makeName;
        }
        if (params.row.carMake?.name) {
          return params.row.carMake.name;
        }
        return 'Unknown';
      }
    },
    { field: 'active', headerName: t('carManagement.active') || 'Active', width: 100, type: 'boolean' },
    {
      field: 'actions',
      headerName: t('common.actions') || 'Actions',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton size="small" onClick={() => params.row && handleOpenModelDialog(params.row as CarModel)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => params.row && params.row.id && handleDeleteModel(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Add a useEffect to log models when tab changes to models
  useEffect(() => {
    if (tabValue === 1) { // Models tab
      console.log("Displaying models tab with data:", models);
    }
  }, [tabValue, models]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="car management tabs">
            <Tab label={t('carManagement.carMakes') || 'Car Makes'} />
            <Tab label={t('carManagement.carModels') || 'Car Models'} />
          </Tabs>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('carManagement.carMakes') || 'Car Makes'}</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenMakeDialog()}
                >
                  {t('carManagement.addMake') || 'Add Make'}
                </Button>
              </Box>
              <div style={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={makes}
                  columns={makeColumns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[5, 10, 20]}
                  checkboxSelection
                  disableRowSelectionOnClick
                />
              </div>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('carManagement.carModels') || 'Car Models'}</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModelDialog()}
                  disabled={makes.length === 0}
                >
                  {t('carManagement.addModel') || 'Add Model'}
                </Button>
              </Box>
              <div style={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={models}
                  columns={modelColumns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[5, 10, 20]}
                  checkboxSelection
                  disableRowSelectionOnClick
                />
              </div>
            </TabPanel>
          </>
        )}
      </Paper>
      
      {/* Make Dialog */}
      <Dialog open={openMakeDialog} onClose={handleCloseMakeDialog}>
        <DialogTitle>{isEditing ? (t('carManagement.editMake') || 'Edit Car Make') : (t('carManagement.addMake') || 'Add Car Make')}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid sx={{ width: '100%' }}>
                <TextField
                  required
                  fullWidth
                  label={t('carManagement.name') || 'Make Name'}
                  value={makeName}
                  onChange={(e) => setMakeName(e.target.value)}
                />
              </Grid>
              <Grid sx={{ width: '100%', mt: 2 }}>
                <TextField
                  fullWidth
                  label={t('carManagement.description') || 'Description'}
                  multiline
                  rows={3}
                  value={makeDescription}
                  onChange={(e) => setMakeDescription(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMakeDialog}>{t('common.cancel') || 'Cancel'}</Button>
          <Button onClick={handleSaveMake} variant="contained">{t('common.save') || 'Save'}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Model Dialog */}
      <Dialog open={openModelDialog} onClose={handleCloseModelDialog}>
        <DialogTitle>{isEditing ? (t('carManagement.editModel') || 'Edit Car Model') : (t('carManagement.addModel') || 'Add Car Model')}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2} direction="column">
              <Grid>
                <TextField
                  select
                  required
                  fullWidth
                  label={t('carManagement.carMakes') || 'Car Make'}
                  value={selectedMakeId || ''}
                  onChange={(e) => setSelectedMakeId(Number(e.target.value))}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">{t('carManagement.selectMake') || 'Select a car make'}</option>
                  {makes.map((make) => (
                    <option key={make.id} value={make.id}>
                      {make.name}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid sx={{ mt: 2 }}>
                <TextField
                  required
                  fullWidth
                  label={t('carManagement.name') || 'Model Name'}
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                />
              </Grid>
              <Grid sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={t('carManagement.description') || 'Description'}
                  multiline
                  rows={3}
                  value={modelDescription}
                  onChange={(e) => setModelDescription(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModelDialog}>{t('common.cancel') || 'Cancel'}</Button>
          <Button onClick={handleSaveModel} variant="contained">{t('common.save') || 'Save'}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CarManagement; 